/**
 * useIdentity — Ecosystem Gap 2: Identity-Driven UX Personalization
 *
 * Returns the user's resolved identity (dominant, submissive, switch, exploring)
 * for the current organization context. Used to personalize empty states,
 * widget ordering, and in-app messaging.
 *
 * Resolution chain (mirrors backend identityService.ts):
 * 1. Backend-resolved identity persisted from Helm API response
 *    (includes dynamic_role, users.identity from registration, legacy role mapping)
 * 2. Organization role mapping fallback (admin -> dominant, member -> submissive)
 * 3. 'exploring' (default)
 *
 * Usage:
 *   const { identity, isDominant, isSubmissive } = useIdentity();
 *   // identity: 'dominant' | 'submissive' | 'switch' | 'exploring'
 */

import { useState, useEffect, useMemo } from 'react';

export type Identity = 'dominant' | 'submissive' | 'switch' | 'exploring';

export interface UseIdentityReturn {
  /** Resolved identity for the current org context */
  identity: Identity;
  /** Convenience: identity === 'dominant' */
  isDominant: boolean;
  /** Convenience: identity === 'submissive' */
  isSubmissive: boolean;
  /** Convenience: identity === 'switch' */
  isSwitch: boolean;
  /** Convenience: identity === 'exploring' */
  isExploring: boolean;
  /** CSS class: 'role-dom' | 'role-sub' | '' (empty for switch/exploring) */
  roleClass: string;
  /** Whether identity is still being resolved */
  loading: boolean;
}

const ORG_STORAGE_KEY = 'selectedOrganization';
const IDENTITY_STORAGE_KEY = 'resolvedIdentity';
const ORG_CHANGED_EVENT = 'organizationChanged';
/** Dispatched when the Helm API response updates the resolved identity */
export const IDENTITY_UPDATED_EVENT = 'identityUpdated';

function getStoredOrgRole(): string | null {
  try {
    const stored = localStorage.getItem(ORG_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.role || null;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

/**
 * Read the resolved identity persisted by HelmDataContext.
 * This is the identity resolved by the backend (identityService fallback chain).
 */
function getStoredResolvedIdentity(): Identity | null {
  try {
    const stored = localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (stored && isValidIdentity(stored)) {
      return stored;
    }
  } catch {
    // Ignore
  }
  return null;
}

function isValidIdentity(value: string): value is Identity {
  return value === 'dominant' || value === 'submissive' || value === 'switch' || value === 'exploring';
}

/**
 * Map the organization role to an identity.
 * This is the synchronous fallback when no resolved identity is available.
 */
function roleToIdentity(role: string | null): Identity {
  // 2026-07-05: was missing the 'owner' org role (a real value, e.g. solo
  // age-verification accounts) — an org owner was falling through to
  // 'exploring' instead of 'dominant'. Same gap existed on the backend
  // mirror (identityService.ts) — fixed there too.
  if (role === 'admin' || role === 'owner') return 'dominant';
  if (role === 'member' || role === 'submissive') return 'submissive';
  return 'exploring';
}

/**
 * Persist the resolved identity from a Helm API response.
 * Called by HelmDataContext when dashboard data arrives.
 */
export function persistResolvedIdentity(identity: string): void {
  if (isValidIdentity(identity)) {
    localStorage.setItem(IDENTITY_STORAGE_KEY, identity);
    window.dispatchEvent(new Event(IDENTITY_UPDATED_EVENT));
  }
}

/**
 * Clear persisted identity (call on logout or org switch).
 */
export function clearResolvedIdentity(): void {
  localStorage.removeItem(IDENTITY_STORAGE_KEY);
  window.dispatchEvent(new Event(IDENTITY_UPDATED_EVENT));
}

export function useIdentity(): UseIdentityReturn {
  const [orgRole, setOrgRole] = useState<string | null>(getStoredOrgRole);
  const [resolvedIdentity, setResolvedIdentity] = useState<Identity | null>(getStoredResolvedIdentity);

  // Listen for org changes
  useEffect(() => {
    const handleOrgChanged = () => {
      setOrgRole(getStoredOrgRole());
      // On org switch, the resolved identity may be stale; re-read
      setResolvedIdentity(getStoredResolvedIdentity());
    };

    window.addEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    return () => {
      window.removeEventListener(ORG_CHANGED_EVENT, handleOrgChanged);
    };
  }, []);

  // Listen for identity updates from Helm API responses
  useEffect(() => {
    const handleIdentityUpdated = () => {
      setResolvedIdentity(getStoredResolvedIdentity());
    };

    window.addEventListener(IDENTITY_UPDATED_EVENT, handleIdentityUpdated);
    return () => {
      window.removeEventListener(IDENTITY_UPDATED_EVENT, handleIdentityUpdated);
    };
  }, []);

  const identity = useMemo<Identity>(() => {
    if (resolvedIdentity) return resolvedIdentity;
    return roleToIdentity(orgRole);
  }, [resolvedIdentity, orgRole]);

  return useMemo(() => ({
    identity,
    isDominant: identity === 'dominant',
    isSubmissive: identity === 'submissive',
    isSwitch: identity === 'switch',
    isExploring: identity === 'exploring',
    roleClass: identity === 'dominant' ? 'role-dom' : identity === 'submissive' ? 'role-sub' : '',
    loading: false,
  }), [identity]);
}

/**
 * Get identity-aware empty state text.
 *
 * Selects the appropriate message based on the user's identity.
 * Falls back to the generic message for 'exploring' or unknown identities.
 *
 * @param messages - Map of identity to message string
 * @param identity - The resolved identity
 * @returns The identity-appropriate message
 */
export function getIdentityMessage(
  messages: Partial<Record<Identity, string>> & { default: string },
  identity: Identity
): string {
  if (identity === 'exploring' || !messages[identity]) {
    return messages.default;
  }
  return messages[identity] || messages.default;
}
